import type { ReactNode } from 'react'
import { track } from '@/lib/analytics'
import { useLocale } from '@/i18n/locale'
import { Term } from '@/components/Term'
import { getOfferDeadline } from '@/lib/offer-deadline'
import { NavLink } from '@/components/NavLink'
import './two-tracks.css'

/**
 * Продукт и стоимость — ОДИН блок (мандат Тони 2026-08-03:
 * «продукт и стоимость соедини в единое, оставь силу»).
 *
 * Мандат по продукту (2026-08-01): продукт один. Обучение, клуб On vAIbe
 * и сопровождение (Хранитель) — ЧАСТЬ продукта, не отдельные SKU. Клуб —
 * бонус внутри продукта (решение Тони 2026-09-20), строка про него не
 * меняется. Для холдингов — та же методология, кастомное ядро: строка,
 * а не второй продукт; после аудита 2026-09-21 эта строка и список
 * подтверждённых секторов живут только в #audience (там же были дословно).
 *
 * Состав продукта на телефоне раскрывается по тапу — нативный <details>
 * на пункт, без JS; на десктопе всё раскрыто, как было (см. two-tracks.css).
 * Термины в названиях — <Term plain>: внутри <summary> кнопка была бы
 * вложенным интерактивом (a11y nested-interactive).
 *
 * Редизайн 2026-09: «от чего зависит цена» и «что вы платите дальше»
 * живут на /vnedrenie-ii-agentov#stoimost, здесь — одна ссылка туда.
 * Фраза про верхнюю границу осталась: её держит страж anchor-phrases,
 * а на странице-приёмнике её нет.
 *
 * id="ladder" сохранён на ценовой части: на него ведут ссылки из
 * калькулятора, из футера и из статей.
 */
type Row = { title: ReactNode; note: string; bonus?: boolean }

const COPY = {
  ru: {
    eyebrow: 'Внедрение ИИ в бизнес под ключ · один продукт, цена — по масштабу',
    heading: 'Продаём не рыбу, а удочку',
    // «Удочка, а не рыба» — самое сильное место звонка 2026-09-25, на нём
    // партнёр и сказал «ты космос». Без «контура» и «корпоративного»: Тони
    // прямо назвал эти слова непонятными собственнику.
    lede: (<>Можно продать рыбу: у вас путается закупщик — сделаем агента. Мы продаём удочку: <Term k="ecosystem">ИИ-экосистему</Term> — инфраструктуру, внутри которой ваша команда сама закрывает закупки, бухгалтерию, финансы, продажи, PR, SEO и контент. Любую новую потребность — без нового подрядчика. Всё работает на лицензиях, оформленных на вашу компанию, и остаётся вашим.</>),

    includedLabel: 'Состав продукта — собирается под ваш масштаб',
    bonus: 'бонус',
    included: [
      { title: 'Личный ИИ-помощник собственника', note: 'с него начинаем: за первые 2 недели вы лично работаете быстрее — в среднем в 2–5 раз' },
      // Замер — часть продукта (Тони 2026-09-27): точка А на старте, точка Б
      // на приёмке, три показателя.
      { title: 'Замер точки А и точки Б', note: 'фиксируем на старте и отслеживаем рост чистой прибыли, рост маржинальной прибыли за счёт сэкономленных расходов и объём задач, который закрывает каждый топ-менеджер, управляя агентами' },
      { title: 'Защищённая рабочая среда компании', note: 'один вход для всей команды, доступ сотрудника отзывается одним действием; данные и наработки принадлежат компании' },
      { title: 'Первый результат на одной задаче', note: 'управленческий учёт, который отвечает в чате: прибыль, платёжный календарь, движение денег — сверено с бухгалтером' },
      { title: 'ИИ-помощник у каждого ключевого руководителя', note: 'принимаем, только если он отвечает на контрольные вопросы 5 из 5; учётная система, CRM, телефония, банки — без ручных выгрузок' },
      { title: 'Обучение команды', note: '15–20 совместных сессий по 1–2,5 часа без отрыва от работы' },
      { title: 'Деньги двигает только человек', note: 'ИИ готовит платёж, но не проводит его: любое списание подтверждает сотрудник' },
      { title: (<><Term k="club" plain>Клуб On vAIbe</Term></>), note: 'отдельно 1 800 $ в год — вам входит в стоимость: практика, шаблоны агентов, разборы', bonus: true },
      { title: (<><Term k="keeper" plain>Хранитель</Term> внутри вашей команды</>), note: 'плюс наш инженер: сопровождение входит в продукт, наработки регулярно выгружаются в ваше хранилище' },
    ] as Row[],

    // Цены на сайте нет (Тони 2026-09-27): стоимость считаем на бесплатной
    // диагностике и фиксируем в договоре. Карточка — условия работы.
    priceTier: 'Условия',
    // Строка с первого экрана, перенесённая сюда Тони 2026-09-27: здесь условия.
    // Срок и аванс стоят строками ниже, поэтому без повтора цифр. Держит точные
    // фразы запроса «внедрение ИИ в бизнес под ключ» и «компания по внедрению
    // ИИ в бизнес» (страж text-budget).
    terms: 'Внедрение ИИ в бизнес под ключ. DNAI\u00a0— инженерная компания по внедрению ИИ в бизнес: каждый этап вы принимаете по проверяемым результатам.',
    facts: [
      ['Срок', '2–3 месяца до приёмки по чек-листам'],
      ['Оплата', 'аванс 30 %, дальше 40 % и 30 % — по приёмке этапов'],
      ['Договор', 'в РФ; счёт в рублях или USDT, эквивалент фиксируем в договоре'],
    ],
    tranchesAria: 'Аванс 30 процентов, затем 40 и 30 процентов по приёмке этапов',
    trancheLabels: ['аванс', 'приёмка', 'приёмка'],
    ceiling: 'Стоимость зависит от числа руководителей, которых подключаем, интеграций и требований к данным: считаем её на бесплатной диагностике и фиксируем в договоре вместе с составом. Остановиться можно после любого этапа: артефакт не принят — следующий транш не выставляется.',
    driversLink: 'От чего зависит стоимость →',
    deadline: (d: string) => `До ${d} берём не больше 4 внедрений в месяц: формат требует 15–20 совместных сессий на клиента. Дальше — очередь.`,
    freeLabel: 'Первый шаг — бесплатно',
    freeText: 'ИИ-диагностика 30–90 минут: карта зрелости, приоритеты по деньгам, черновик дорожной карты.',
    cta: 'Записаться на диагностику',
  },
  en: {
    eyebrow: 'Turnkey AI implementation · One product, priced by scale',
    heading: 'We sell the rod, not the fish',
    lede: (<>You could buy the fish: your buyer keeps mixing things up — we build an agent. We sell the rod: an <Term k="ecosystem">AI ecosystem</Term>, the infrastructure inside which your own team handles purchasing, accounting, finance, sales, PR, SEO and content. Any new need — without a new contractor. It all runs on licenses registered to your company and stays yours.</>),

    includedLabel: 'What the product is made of — scoped to your scale',
    bonus: 'bonus',
    included: [
      { title: 'The owner’s personal AI assistant', note: 'we start here: within the first 2 weeks you personally work 2–5× faster on average' },
      { title: 'Point A and point B, measured', note: 'recorded at the start and tracked: net profit growth, margin growth from saved costs, and the volume of tasks each executive closes by managing agents' },
      { title: 'A secure company workspace', note: 'one sign-in for the whole team, an employee’s access revoked in one action; data and work belong to the company' },
      { title: 'A first result on one task', note: 'management accounting that answers in chat: profit, payment calendar, cash flow — checked with the accountant' },
      { title: 'An AI assistant for each key executive', note: 'accepted only when it answers control questions 5 out of 5; the accounting system, CRM, telephony and banks connected with no manual exports' },
      { title: 'Team training', note: '15–20 joint sessions of 1–2.5 hours without stepping away from work' },
      { title: 'Only a person moves money', note: 'AI prepares a payment but never sends it: every payment is confirmed by an employee' },
      { title: (<><Term k="club" plain>The On vAIbe club</Term></>), note: '$1,800 a year standalone — included for you: practice, agent templates, teardowns', bonus: true },
      { title: (<>A <Term k="keeper" plain>Keeper</Term> inside your team</>), note: 'plus our engineer: ongoing support is part of the product, your work is regularly exported into your own storage' },
    ] as Row[],

    priceTier: 'Terms',
    terms: 'Turnkey AI implementation for business. DNAI is an engineering company for AI implementation in business: you accept every stage by verifiable results.',
    facts: [
      ['Timeline', '2–3 months to checklist sign-off'],
      ['Payment', '30% upfront, then 40% and 30% on stage acceptance'],
    ],
    tranchesAria: '30 percent upfront, then 40 and 30 percent on stage acceptance',
    trancheLabels: ['upfront', 'acceptance', 'acceptance'],
    ceiling: 'The cost depends on the number of executives we connect, integrations and data requirements: we calculate it at the free diagnostics and fix it in the contract together with the scope. You can stop after any stage: if an artifact is not accepted, the next tranche is not invoiced.',
    driversLink: 'What moves the cost →',
    deadline: (d: string) => `Until ${d} we take no more than 4 rollouts a month: the format requires 15–20 joint sessions per client. After that — a queue.`,
    freeLabel: 'The first step is free',
    freeText: 'AI diagnostics, 30–90 minutes: a maturity map, money priorities, a draft roadmap.',
    cta: 'Book AI diagnostics',
  },
}

const TRANCHES = [30, 40, 30]
const LABEL = 'font-mono text-small uppercase tracking-wider'

export function TwoTracks() {
  const locale = useLocale()
  const c = COPY[locale]
  const deadline = getOfferDeadline()
  const deadlineText = locale === 'en' ? deadline.humanEn : deadline.human

  return (
    <section className="section" id="tracks">
      <div className="container-x relative z-10">
        {/* Порядок в разметке — мобильный: заголовок → цена → раскрытие.
            На телефоне колонки складываются в стопку, и если цену оставить
            последней, до неё нужно прочитать весь состав — ровно та
            дистанция между «что покупаю» и «сколько стоит», которую этот
            блок и убирает. На широком экране grid возвращает цену вправо.
            grid-rows-[auto_1fr] обязателен: карточка цены занимает обе
            строки, и без явных высот её рост растягивал бы первую. */}
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:grid-rows-[auto_1fr] gap-x-12 xl:gap-x-20 gap-y-10 items-start">
          <div className="lg:col-span-7 lg:col-start-1 lg:row-start-1">
            <p className={`${LABEL} text-muted mb-5`}>{c.eyebrow}</p>
            <h2 className="text-h2 text-ink">{c.heading}</h2>
          </div>

          {/* self-stretch: ячейка тянется на высоту левой колонки, и
              sticky-карточке есть куда ехать, пока читают состав. */}
          <div
            id="ladder"
            className="lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:row-span-2 lg:self-stretch scroll-mt-24"
          >
            <article className="relative bg-card border border-divider rounded-card p-6 sm:p-8 xl:p-10 lg:sticky lg:top-24">
              <div className={`${LABEL} text-muted`}>{c.priceTier}</div>

              <p className="mt-4 text-body text-ink leading-relaxed">{c.terms}</p>

              <dl className="mt-6 border-t border-divider">
                {c.facts.map(([k, v], i) => (
                  <div key={k} className="grid grid-cols-[6.5rem_1fr] gap-x-4 py-3.5 border-b border-divider">
                    <dt className={`${LABEL} text-muted pt-[0.1em]`}>{k}</dt>
                    <dd className="text-small text-ink leading-snug">
                      {v}
                      {i === 1 && (
                        // Длина полос кодирует долю транша — тот же приём,
                        // что у сроков в блоке «Как это устроено».
                        <div className="mt-3 flex gap-1" role="img" aria-label={c.tranchesAria}>
                          {TRANCHES.map((t, j) => (
                            <div key={j} style={{ flexGrow: t }} className="basis-0 min-w-0">
                              <div className="h-1.5 rounded-full bg-accent" style={{ opacity: 0.45 + j * 0.25 }} />
                              <div className="mt-1.5 font-mono text-small text-muted tabular-nums">{t}%</div>
                              <div className="font-mono text-[0.6875rem] text-muted leading-tight">{c.trancheLabels[j]}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>

              <p className="mt-5 text-small text-muted leading-snug">{c.ceiling}</p>
              {locale === 'ru' && (
                <NavLink
                  href="/vnedrenie-ii-agentov#stoimost"
                  className="inline-block mt-2 text-small text-ink underline decoration-divider underline-offset-4 hover:decoration-ink"
                >
                  {c.driversLink}
                </NavLink>
              )}

              <div className="mt-7 pt-6 border-t border-divider">
                <div className="flex items-center gap-2.5">
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-accent" />
                  <span className={`${LABEL} text-ink`}>{c.freeLabel}</span>
                </div>
                <p className="mt-2 text-small text-muted leading-snug">{c.freeText}</p>
                <NavLink
                  href="#final-cta"
                  onClick={() => track('ladder_cta_click', { rung: 'product' })}
                  className="btn-cta no-underline text-center mt-5 w-full"
                >
                  {c.cta}
                </NavLink>
                <p className="mt-4 text-small text-muted leading-snug">{c.deadline(deadlineText)}</p>
              </div>
            </article>
          </div>

          <div className="lg:col-span-7 lg:col-start-1 lg:row-start-2">
            <p className="text-body text-ink leading-relaxed max-w-[62ch]">{c.lede}</p>

            {/* Состав — один список-таблица: название слева, деталь справа.
                На телефоне (< 640 px) деталь спрятана за тапом по названию:
                <summary> — вся строка, зона нажатия ≥ 44 px, «+» справа. */}
            <div className={`${LABEL} text-muted mt-12 mb-4`}>{c.includedLabel}</div>
            <ol className="border-b border-divider">
              {c.included.map((row, i) => (
                <li
                  key={i}
                  className="border-t border-divider sm:grid sm:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] sm:gap-x-4 sm:py-4"
                >
                  <details className="tt-row">
                    <summary className="tt-sum grid grid-cols-[1.75rem_minmax(0,1fr)_1rem] sm:grid-cols-[2.5rem_minmax(0,1fr)] gap-x-3 sm:gap-x-4 items-start min-h-[44px] py-2.5 sm:min-h-0 sm:py-0 cursor-pointer sm:cursor-default">
                      <span aria-hidden className="font-mono text-small text-muted tabular-nums pt-[0.2em]">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="text-body text-ink font-medium leading-snug">
                        {row.title}
                        {row.bonus && (
                          <span className="ml-2.5 inline-flex items-center gap-1.5 align-middle font-mono text-[0.75rem] font-normal uppercase tracking-wider text-ink border border-divider rounded-full px-2 py-0.5">
                            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-accent" />
                            {c.bonus}
                          </span>
                        )}
                      </span>
                      <span aria-hidden className="tt-chev inline-block sm:hidden font-mono text-h3 text-muted leading-none justify-self-end">
                        +
                      </span>
                    </summary>
                  </details>
                  <p className="tt-note pl-10 pb-4 sm:pl-0 sm:pb-0 text-small text-muted leading-snug">{row.note}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  )
}
